import { useState } from 'react';
import { useOwners, useSetWebAccess, useTransfer, useTransferActions } from '../api/hooks';
import { useSession } from '../session';
import { Empty, ErrorState, Loading } from '../components/States';
import { ApiError } from '../api/client';

export default function Owners() {
  const { user } = useSession();
  const isSuper = Boolean(user?.is_super_admin);
  const { data, isPending, error, refetch } = useOwners();
  const setAccess = useSetWebAccess();

  if (isPending) return <Loading what="the owners" />;
  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;
  if (!data?.length) return <Empty title="No owners yet" />;

  return (
    <>
      <div className="section__head">
        <h1 className="section__title">Owners</h1>
        <p className="section__sub muted">
          {isSuper ? 'You are the Super Admin. Web access is yours to grant.' : 'Only the Super Admin can change these.'}
        </p>
      </div>

      <div className="card" style={{ padding: 0, marginBottom: 'var(--s-xl)' }}>
        {data.map((o) => (
          <div className="row" key={o.id}>
            <div>
              <div style={{ fontWeight: 600 }}>
                {o.name}
                {o.is_super_admin && <span className="pill pill--quiet" style={{ marginLeft: 8 }}>Super Admin</span>}
                {String(o.id) === String(user?.id) && <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>you</span>}
              </div>
              <div className="muted" style={{ fontSize: 12 }}>{o.email}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-md)' }}>
              {o.web_app_access
                ? <span className="pill pill--ok">Web access</span>
                : <span className="pill pill--quiet">App only</span>}
              {/* The super admin holds access implicitly and cannot be toggled. */}
              {isSuper && !o.is_super_admin && (
                <button
                  className="btn"
                  disabled={setAccess.isPending}
                  onClick={() => setAccess.mutate({ userId: o.id, enabled: !o.web_app_access })}
                >
                  {o.web_app_access ? 'Revoke' : 'Grant'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* There is no `last_active` field on User, and it will not be invented. */}
      <p className="muted" style={{ fontSize: 12, maxWidth: '62ch', marginTop: 0 }}>
        Revoking web access takes effect on this person's next request. It does <strong>not</strong>{' '}
        sign them out of the Android app — their owner rights there are unchanged, because this is a
        product entitlement, not a data boundary.
      </p>

      {isSuper && <Transfer owners={data.filter((o) => !o.is_super_admin)} />}
    </>
  );
}

/** §7.4. Two things the UI must say before the button is pressed, and does. */
function Transfer({ owners }: { owners: { id: string; name: string }[] }) {
  const { data: pending, isPending } = useTransfer();
  const { request, confirm, cancel } = useTransferActions();
  const [target, setTarget] = useState('');
  const [code, setCode] = useState('');

  const err = (e: unknown) => (e instanceof ApiError ? e.message : null);
  const anyError = err(request.error) ?? err(confirm.error) ?? err(cancel.error);

  // If the organisation has only one owner, transfer is impossible. Say so and
  // disable it, rather than failing after the code has been sent.
  const impossible = owners.length === 0;

  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">Transfer Super Admin</h2>
        <p className="section__sub muted">Hands over who may grant web access, and who may transfer the role again.</p>
      </div>

      <div className="card">
        {impossible ? (
          <p className="muted" style={{ margin: 0 }}>
            There is no one to transfer to. Invite a second owner first — the role can only move to
            an approved owner in this organisation.
          </p>
        ) : isPending ? (
          <p className="muted" style={{ margin: 0 }}>Checking…</p>
        ) : pending ? (
          <>
            <p style={{ marginTop: 0 }}>
              A transfer to <strong>{pending.to_name}</strong> is waiting for the code emailed to you.
              <span className="muted"> {pending.attempts_left} attempt{pending.attempts_left === 1 ? '' : 's'} left.</span>
            </p>
            <div style={{ display: 'flex', gap: 'var(--s-sm)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label className="label" htmlFor="code">Confirmation code</label>
                <input id="code" className="field" inputMode="numeric" maxLength={6}
                  value={code} onChange={(e) => setCode(e.target.value)} placeholder="6 digits" />
              </div>
              <button className="btn btn--gold" disabled={confirm.isPending || code.length < 6}
                onClick={() => confirm.mutate(code)}>Confirm transfer</button>
              <button className="btn btn--quiet" disabled={cancel.isPending} onClick={() => cancel.mutate()}>Cancel</button>
            </div>
            <p className="warnbox">
              When you confirm, <strong>you will both be signed out</strong> of the app and the console
              and will need to log in again. You will remain an owner and keep web access.
            </p>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 'var(--s-sm)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 220px' }}>
                <label className="label" htmlFor="target">New Super Admin</label>
                <select id="target" className="field" value={target} onChange={(e) => setTarget(e.target.value)}>
                  <option value="">Choose an owner…</option>
                  {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <button className="btn" disabled={!target || request.isPending}
                onClick={() => request.mutate(target)}>Send me a code</button>
            </div>
            <p className="warnbox">
              The code goes to <strong>your</strong> email, not theirs — it is what proves you agreed.
              Nothing changes until you enter it.
            </p>
          </>
        )}

        {anyError && <p className="warnbox warnbox--bad" role="alert">{anyError}</p>}
        {confirm.isSuccess && <p className="warnbox warnbox--ok">{confirm.data?.message}</p>}
      </div>
    </section>
  );
}
